import { useState, useEffect } from "react";
import Wheel from "../components/Wheel.jsx";
import Congratulations from "../components/Congratulations.jsx";
import HardLuck from "../components/HardLuck.jsx";
import { fetchPrizes, fetchAvailablePrizes, checkSpinStatus, recordSpin, selectPrizeForSpin, seedPrizes } from "../utils/api.js";
import { UI_CONFIG, WHEEL_CONFIG, API_BASE_URL } from "../constants/index";

export default function WheelPage({ userEmail, onPrizeWon, showCongratulationsDirectly = false, onBackToStory }) {
  const [prizes, setPrizes] = useState([]);
  const [isSpinning, setIsSpinning] = useState(false);
  const [selectedPrize, setSelectedPrize] = useState(null);
  const [userSpinStatus, setUserSpinStatus] = useState({ 
    isAllowedToSpin: true, 
    hasSpun: false 
  });
  const [showCongratulations, setShowCongratulations] = useState(false);
  const [showHardLuck, setShowHardLuck] = useState(false);
  const [previousPrize, setPreviousPrize] = useState(null);
  const [predeterminedPrize, setPredeterminedPrize] = useState(null);
  const [spinId, setSpinId] = useState(null);

  useEffect(() => {
    loadInitialData();
  }, [userEmail]);

  useEffect(() => {
    if (showCongratulationsDirectly && userEmail) {
      handleDirectCongratulations();
    }
  }, [showCongratulationsDirectly, userEmail]);



  const loadInitialData = async () => {
    await fetchPrizesData();
    if (userEmail) {
      await checkUserSpinStatusData(userEmail);
    }
  };

  const handleDirectCongratulations = async () => {
    try {
      // Fetch the user's spin result to get their prize information
      const response = await fetch(`${API_BASE_URL}/api/users/spin-result/${encodeURIComponent(userEmail)}`);
      if (response.ok) {
        const result = await response.json();
        const userData = result.data || result;
        
        if (userData.hasSpun && userData.prize) {
          setSelectedPrize(userData.prize);
          setUserSpinStatus({ isAllowedToSpin: false, hasSpun: true });
          
          // Check if the prize is "Rien" or "Aucun prix" to show appropriate page
          const prizeName = userData.prize.name.toLowerCase();
          if (prizeName === 'rien' || prizeName === 'aucun prix') {
            setShowHardLuck(true);
          } else {
            setShowCongratulations(true);
          }
        }
      }
    } catch (error) {
      console.error('Error fetching user spin result:', error);
    }
  };

  const fetchPrizesData = async () => {
    try {
      // Use the new endpoint that only returns available prizes
      const availablePrizes = await fetchAvailablePrizes();
      // console.log('✅ Available prizes (from backend):', availablePrizes);
      setPrizes(availablePrizes);
    } catch (error) {
      // console.error('Erreur lors de la récupération des prix :', error);
    }
  };
  
  const checkUserSpinStatusData = async (email) => {
    try {
      const status = await checkSpinStatus(email);

      const userData = status.data || status;
      
      setUserSpinStatus({
        isAllowedToSpin: userData.isAllowedToSpin || false,
        hasSpun: !userData.isAllowedToSpin
      });

      // If user has already spun but hasn't received email, show congratulations page
      if (!userData.isAllowedToSpin && !userData.emailSent) {
        // User needs to claim their prize - we'll need to get their prize info
        // For now, we'll let them proceed to the wheel page which will handle this
        // console.log('User has spun but needs to claim prize');
      }
    } catch (error) {
      // console.error('Erreur lors de la vérification du statut de spin :', error);
    }
  };

  const handleSpinClick = async () => {
    if (isSpinning || !userEmail || userSpinStatus.hasSpun) return;
    
    try {
      // First, get the predetermined prize from the backend
      const result = await selectPrizeForSpin(userEmail);
      const { prize, spinId: newSpinId } = result.data || result;
      
      // console.log('🎁 Backend selected prize:', prize);
      // console.log('🔑 Spin ID:', newSpinId);
      
      setPredeterminedPrize(prize);
      setSpinId(newSpinId);
      setIsSpinning(true);
    } catch (error) {
      console.error('Error selecting prize:', error);
      // Handle error - maybe show a message to the user
    }
  };

  const handleSpinComplete = async (winningPrize) => {
    // Prevent multiple executions
    if (userSpinStatus.hasSpun || !winningPrize || !spinId) return;
    
    try {
      // Record the spin with the predetermined prize using the spinId
      await recordSpin(userEmail, spinId);
      setSelectedPrize(winningPrize);
      setUserSpinStatus({ isAllowedToSpin: false, hasSpun: true });
      // Check if the prize is "Rien" or "Aucun prix" to show appropriate page
      const prizeName = winningPrize.name.toLowerCase();
      if (prizeName === 'rien' || prizeName === 'aucun prix') {
        setShowHardLuck(true);
      } else {
        setShowCongratulations(true);
      }
    } catch (error) {
      console.error('Erreur lors de l\'enregistrement du spin :', error);
    }
    setIsSpinning(false);
  };



  const handleHardLuckContinue = () => {
    // For now, just hide the hard luck page
    // You can add navigation logic here if needed
    setShowHardLuck(false);
  };

  const handleBackToStory = () => {
    // Go back to the story sequence
    if (onBackToStory) {
      onBackToStory();
    }
  };

  const handleCongratulationsContinue = async () => {
    try {
      const userEmail = localStorage.getItem("userEmail");
      const userName = localStorage.getItem("userFirstName");
      
      if (!userEmail || !userName) {
        console.error('User data not found');
        return;
      }

      const response = await fetch(`${API_BASE_URL}/api/users/send-prize-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: userName,
          email: userEmail,
          prize: selectedPrize?.name || 'Prix gagné',
        }),
      });

      if (response.ok) {
        // console.log('Email sent successfully');
        // After successful email, trigger the prize won callback to show success page
        onPrizeWon(selectedPrize);
      } else {
        console.error('Failed to send email');
      }
    } catch (error) {
      console.error('Error sending email:', error);
    }
    
    setShowCongratulations(false);
  };

  const handleReloadPrizes = () => {
    fetchPrizesData();
  };

  const handleSeedPrizes = async () => {
    try {
      await seedPrizes();
      fetchPrizesData();
    } catch (error) {
      // console.error('Erreur lors de la création des prix :', error);
    }
  };

  const handleClaimPreviousPrize = async () => {
    try {
      const userEmail = localStorage.getItem("userEmail");
      const userName = localStorage.getItem("userFirstName");
      
      if (!userEmail || !userName) {
        console.error('User data not found');
        return;
      }

      const response = await fetch(`${API_BASE_URL}/api/users/send-prize-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: userName,
          email: userEmail,
          prize: previousPrize?.name || 'Prix gagné',
        }),
      });

      if (response.ok) {
        alert('Email envoyé avec succès ! Vérifiez votre boîte de réception.');
      } else {
        alert('Erreur lors de l\'envoi de l\'email. Veuillez réessayer.');
      }
    } catch (error) {
      console.error('Error sending email:', error);
      alert('Erreur lors de l\'envoi de l\'email. Veuillez réessayer.');
    }
  };

  const renderNoPrizesMessage = () => (
    <div className="mb-8 text-center">
      <div className="bg-gray-50 rounded-2xl p-8 max-w-md mx-auto border border-gray-100">
        <svg className="w-24 h-24 text-gray-400 mx-auto mb-4" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
        </svg>
        <h3 className="text-lg font-semibold text-gray-800 mb-2">Aucun prix disponible</h3>
        <p className="text-gray-600 text-sm">Veuillez réessayer plus tard</p>
        <div className="mt-4 space-x-2">
          <button 
            onClick={handleReloadPrizes} 
            className="bg-orange-500 text-white px-4 py-2 rounded-lg text-sm"
          >
            Recharger les prix
          </button>
          <button 
            onClick={handleSeedPrizes}
            className="bg-green-500 text-white px-4 py-2 rounded-lg text-sm"
          >
            Créer des prix
          </button>
        </div>
      </div>
    </div>
  );

  const renderHeader = () => (
    <div className="flex items-center pb-8">
      <div className={`${UI_CONFIG.LOGO_SIZE.MEDIUM}`}>
        <img 
          src="/static/juniorisep.svg" 
          alt="Logo" 
          className="w-full h-full object-contain"
        />
      </div>
    </div>
  );

  const renderContent = () => (
    <div className="flex-1 flex flex-col justify-center max-w-lg">

      {prizes && prizes.length > 0 ? (
        <div className="mb-8">
                  <Wheel
          prizes={prizes}
          onSpin={handleSpinComplete}
          onSpinClick={handleSpinClick}
          isSpinning={isSpinning}
          hasSpun={userSpinStatus.hasSpun}
          predeterminedPrize={predeterminedPrize}
        />
          
          {userSpinStatus.hasSpun && previousPrize && (
            <div className="mt-8 text-center">
              <div className="bg-green-50 border border-green-200 rounded-xl p-6 max-w-md mx-auto">
                <div className="flex items-center justify-center mb-3">
                  <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-green-800 mb-2">
                  Félicitations ! 🎉
                </h3>
                <p className="text-green-700 text-sm mb-3">
                  Vous avez gagné : <span className="font-bold">{previousPrize.name}</span>
                </p>
                <button
                  onClick={handleClaimPreviousPrize}
                  className="bg-green-600 text-white px-6 py-2 rounded-lg text-sm font-medium hover:bg-green-700 transition-colors"
                >
                  Récupérer mon prix
                </button>
              </div>
            </div>
          )}
        </div>
      ) : (
        renderNoPrizesMessage()
      )}
    </div>
  );

  if (showCongratulations && selectedPrize) {
    return (
      <div className="fixed inset-0 z-50">
        <div className="absolute bottom-0 left-0 right-0 h-full bg-white">
          <Congratulations 
            prize={selectedPrize} 
            userEmail={userEmail}
            onContinue={handleCongratulationsContinue}
          />
        </div>
      </div>
    );
  }

  if (showHardLuck) {
    return (
      <div className="fixed inset-0 z-50">
        <div className="absolute bottom-0 left-0 right-0 h-full bg-white">
          <HardLuck 
            onContinue={handleHardLuckContinue}
            onBackToStory={handleBackToStory}
            userEmail={userEmail}
          />
        </div>
      </div>
    );
  }



  return (
    <div className="px-4 md:px-10 w-full h-screen bg-white flex flex-col overflow-hidden">
      <div className="flex-1 flex flex-col justify-center overflow-y-auto">
        {renderHeader()}
        {renderContent()}
      </div>
    </div>
  );
}
